/**
 * PanelHeader — the row along the top of a panel: 44px tall, with its buttons
 * 32px square and 6px off the panel's top and right edges. The geometry lives
 * in occurra.css (section 5), so every panel's header is the same row.
 *
 * PanelHost draws one for a headered panel, with the corner row (the panel's
 * own buttons and the ×) at its end. A headerless panel draws its own and lets
 * PanelHost float that corner row over it: `reserve` keeps the content clear of
 * the row, measured, so the room tracks the buttons a panel shows right now.
 *
 * A pane that does not reach the panel's right edge — a list rail beside an
 * editor — takes no reserve, and puts its own buttons in `end`.
 */

import type { ReactElement, ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface Props {
    children?: ReactNode;
    /** Buttons at this row's own right end, such as a rail's collapse chevron. */
    end?: ReactNode;
    /** Keep clear of the corner row PanelHost floats over a headerless panel. */
    shouldReserve?: boolean;
    /** A rule along the bottom edge. */
    isDivided?: boolean;
    /** Drawn over a picture: a pill starts the row instead of a title. */
    isOverPicture?: boolean;
    className?: string;
}

export const PanelHeader = ({
    children, end, shouldReserve = false, isDivided = false, isOverPicture = false, className,
}: Props): ReactElement => (
    <header
        data-reserve={shouldReserve || undefined}
        data-divided={isDivided || undefined}
        data-over-picture={isOverPicture || undefined}
        className={cn('occ-panel-header', className)}
    >
        {children}
        {end && <div className="occ-panel-header-end">{end}</div>}
    </header>
);
